import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import {
  ASCENDING,
  DESCENDING,
  type ClientTableOptions,
  type ClientTableResult,
  type ClientTableState,
  type SortDirection,
} from './types';

export function useClientTable<TData extends object>({
  columns,
  data,
  initialPageNumber = 1,
  initialPageSize = 25,
  initialFilter = '',
  initialSortKey,
  initialSortDirection,
  onChange,
}: ClientTableOptions<TData>): ClientTableResult<TData> {
  const [globalFilter, setGlobalFilter] = useState(initialFilter);
  const [sorting, setSorting] = useState<SortingState>(
    initialSortKey
      ? [
          {
            id: initialSortKey,
            desc: initialSortDirection === DESCENDING,
          },
        ]
      : []
  );
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initialPageNumber - 1,
    pageSize: initialPageSize,
  });

  const columnDefs = useMemo<ColumnDef<TData>[]>(
    () =>
      columns.map((col) => ({
        id: String(col.key),
        accessorKey: col.key as string,
        header: col.header,
      })),
    [columns]
  );

  const table = useReactTable({
    columns: columnDefs,
    data,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    sortDescFirst: false,
    state: { globalFilter, sorting, pagination },
  });

  const activeSort = sorting[0];
  const sortKey = (activeSort?.id ?? null) as (keyof TData & string) | null;
  const sortDirection: SortDirection = activeSort
    ? activeSort.desc
      ? DESCENDING
      : ASCENDING
    : undefined;

  const totalRows = data.length === 0 ? 0 : table.getFilteredRowModel().rows.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(totalRows / pageSize) || 1);
  // TanStack's setPageIndex / nextPage don't clamp when `pageCount` isn't set as
  // an option, so consumers can overshoot. Clamp here so derived outputs stay sane.
  const lastPageIndex = Math.max(0, pageCount - 1);
  const pageIndex = Math.min(pagination.pageIndex, lastPageIndex);
  const pageNumber = pageIndex + 1;
  const rangeStart = totalRows === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = Math.min(totalRows, (pageIndex + 1) * pageSize);

  const state = useMemo<ClientTableState<TData>>(
    () => ({
      filter: globalFilter,
      pageNumber,
      pageSize,
      sortKey,
      sortDirection,
    }),
    [globalFilter, pageNumber, pageSize, sortKey, sortDirection]
  );

  // Keep the latest onChange reachable from the effect without re-firing when the
  // parent passes a fresh function each render.
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    onChangeRef.current?.(state);
  }, [state]);

  return {
    headers: columns.map((col) => {
      const id = String(col.key);
      const isActive = sortKey === id;
      return {
        id,
        label: col.header,
        sort: {
          active: isActive,
          direction: isActive ? sortDirection : undefined,
          toggle: () => table.getColumn(id)?.toggleSorting(),
          set: (direction) => {
            if (direction === undefined) {
              table.setSorting([]);
              return;
            }
            table.setSorting([{ id, desc: direction === DESCENDING }]);
          },
        },
      };
    }),
    filter: {
      set: setGlobalFilter,
      value: globalFilter,
    },
    pagination: {
      canNext: pageIndex < lastPageIndex,
      canPrevious: pageIndex > 0,
      pageCount,
      pageNumber,
      pageSize,
      rangeEnd,
      rangeStart,
      totalRows,
      goTo: (target: number) => table.setPageIndex(target - 1),
      next: () => table.nextPage(),
      previous: () => table.previousPage(),
    },
    rows: table.getRowModel().rows.map((row) => row.original),
  };
}
